import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import UnitSelect from '../../components/UnitSelect';
import { useAuth } from '../../components/AuthContext';
import { notifyRequestOrdered, notifyPurchaseApprovalNeeded, notifyPurchaseApprovalDenied, notifyPurchaseApprovalGranted } from '../../lib/materialRequestEmails';
import { canContactVendor, canActionPurchasingQueue, canAddVendor, canApprovePurchaseThreshold, canCloseLineForReceiving } from '../../lib/permissions';
import OnboardingCallout from '../../components/OnboardingCallout';
import Snackbar, { useSnackbar } from '../../components/Snackbar';
import { RECEIPT, setLineCloseFlag } from '../../lib/shortClose';
import { checkVendorSimilarity } from '../../lib/vendorDedup';
import { sendNotification } from '../../lib/notify';
import { fetchProvisionalVendorItem } from '../../lib/materialReceive';
import Overlay from '../../components/Overlay';
import VendorContactCard from '../../components/inventory/VendorContactCard';
import TrackingRow from '../../components/inventory/TrackingRow';
import ShortCloseSheet from '../../components/inventory/ShortCloseSheet';
import { itemLabel, itemUom, trackingSummary, trackingLabel, preferredVendorPicks } from '../../lib/requestItems';
import { fmtDate } from '../../lib/utils';
import {
  BRANCHES,
  toDateInputValue,
  toMoneyInput,
  needsSpendApproval,
  ageDays,
  requestedSummary,
  poNumberMatchesFormat,
} from '../../lib/purchasingQueue';
import { matchesOrderSearch, matchedIdentifierKey } from '../../lib/orderIdentifiers';
import AgeBadge from '../../components/purchasing/AgeBadge';
import ConfirmationBadge from '../../components/purchasing/ConfirmationBadge';
import ApprovedToOrderBanner from '../../components/purchasing/ApprovedToOrderBanner';
import VendorOrderNumberRow from '../../components/purchasing/VendorOrderNumberRow';
import QueueTabs from '../../components/purchasing/QueueTabs';
import TrackingSheet from '../../components/purchasing/TrackingSheet';
import CancelConfirmSheet from '../../components/purchasing/CancelConfirmSheet';
import { cancelMaterialRequest } from '../../lib/materialRequests';
import ApprovalSheet from '../../components/purchasing/ApprovalSheet';
import RequestCard from '../../components/purchasing/RequestCard';
import DetailPanel from '../../components/purchasing/DetailPanel';
import MobileDetailSheet from '../../components/purchasing/MobileDetailSheet';
import MarkOrderedSheet from '../../components/purchasing/MarkOrderedSheet';
import SendOrderSection from '../../components/purchasing/SendOrderSection';
import { buildPurchaseOrder, purchaseOrderBlockers } from '../../lib/purchaseOrder';
import { deliverPurchaseOrder } from '../../lib/purchaseOrderSend';
import PurchaseOrderDownload from '../../components/purchasing/PurchaseOrderDownload';
import StoredImage from '../../components/StoredImage';
import { INVENTORY_BUCKET } from '../../lib/storedFile';
import {
  IconPhone,
  IconMail,
  IconSearch,
  IconPackage,
  IconCheck,
  IconTruck,
  IconPlus,
  IconX,
  IconBuildingStore,
  IconStar,
  IconChevronDown,
  IconAlertTriangle,
  IconCopy,
  IconClock,
} from '@tabler/icons-react';


// SELL_UNIT_OPTIONS retired at Session 78n2, #398. It was one of SIX unit
// vocabularies in the app and the only one with its OWN nine words - the other
// three hardcoded lists carried sqft and lnft, which this one did not. That gap
// is not cosmetic: an off-catalog request for 100 sqft of vinyl seeded this
// field with 'sqft', a value the dropdown had no option for, and if purchasing
// never touched the control 'sqft' was still what got saved. A value the
// dropdown claims is impossible, stored through that dropdown.
// The governed list is now the only source. See components/UnitSelect.js.

// #256. Off-catalog restore needs the vendor record itself, not just its id - the
// provisional vendor_items row carries vendor_id, and moVendor holds an object.
async function fetchVendorById(vendorId) {
  if (!vendorId) return null;
  const { data } = await supabase
    .from('inventory_vendors')
    .select('id, name, contact_name, email, phone, payment_terms, is_internal, branch_id')
    .eq('id', vendorId)
    .maybeSingle();
  return data || null;
}

// Every column both reads of this table need. ONE copy, because #304's settled
// lookup returns rows that go straight into the same RequestCard and DetailPanel
// as the live queue — so a column present in one read and missing from the other
// renders as a blank field on some cards and not others, which is the exact
// "reads as a rendering fault" problem D15 exists to stop.
const REQUEST_SELECT = `
          id, request_number, branch_id, status, notes, created_at, updated_at,
          ordered_at,
          department_id, po_document,
          work_order_ref, po_ref, vendor_order_ref, vendor_id, tracking_carrier, tracking_number, tracking_eta,
          ordered_price, invoice_price, invoice_ref, invoiced_at, price_discrepancy_flagged, price_discrepancy_amount,
          approval_threshold_amount, approved_by, approved_at, approved_amount, denial_reason,
          requested_by, assigned_to,
          material_request_items (
            id, item_id, qty_requested, qty_received, notes,
            is_off_catalog, off_catalog_description, off_catalog_uom,
            off_catalog_vendor_name, off_catalog_vendor_id, photo_url, needed_by,
            price_per_pack, packs_ordered, ordered_price, invoice_price,
            receipt_closed, receipt_closed_at, receipt_close_reason,
            invoice_closed, invoice_closed_at, invoice_close_reason,
            tracking_carrier, tracking_number, tracking_eta,
            receipt_closed_by_user:users!material_request_items_receipt_closed_by_fkey ( id, full_name ),
            invoice_closed_by_user:users!material_request_items_invoice_closed_by_fkey ( id, full_name ),
            material_request_invoice_lines ( id, invoice_id, qty_invoiced, line_amount ),
            inventory_items ( id, name, sku, uom,
              vendor_items ( id, vendor_id, vendor_item_number, vendor_price, unit_cost, pack_qty, is_preferred, price_locked,
                inventory_vendors ( id, name, contact_name, email, phone )
              )
            )
          ),
          users!material_requests_requested_by_fkey ( id, full_name ),
          approver:users!material_requests_approved_by_fkey ( id, full_name )
`;

// D9. Closed history grows forever, so the settled lookup is capped. The number
// matches the one accounts payable's Settled tab already uses rather than
// inventing a second, and it is written on screen rather than left to look like
// the whole truth.
const SETTLED_CAP = 50;

// ── TOP-LEVEL COMPONENTS (hoisted to prevent remount bug) ─────────────────────

export default function PurchasingQueuePage({ setActive }) {
  const { profile, isAdmin, isManager, isExecutive, isPurchasing, isTeamMember } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [settings, setSettings] = useState(null);

  // #395, Session 78n8.2. The addresses a purchase order prints as its Ship To.
  //
  // These come from the `branches` TABLE rather than from the BRANCHES constant
  // at the top of this file, and the difference is the point: that constant is
  // #252's hardcoded four-branch list -- id and name only -- and it is what
  // every label on this page reads. It has no street, no city and no ZIP,
  // because until 78n8 those columns did not exist anywhere.
  //
  // This is deliberately ADDITIVE rather than a replacement. Unwinding #252 is
  // thirteen files and is hosted at the January 2027 rollout; nothing here
  // touches it. The constant keeps naming branches and this supplies the one
  // thing a document needs and it cannot give.
  const [branchRows, setBranchRows] = useState([]);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);
  const [mobileDetailReq, setMobileDetailReq] = useState(null);
  const [activeTab, setActiveTab] = useState('needs_action');

  // ── #304 · the search ──────────────────────────────────────────────────────
  // D2. A settled order leaving the On order tab is NOT the defect — every
  // system studied removes it, and On order is a chase-the-vendor list. What
  // none of them does is leave the buyer unable to retrieve it, and until now
  // this page had no search of any kind while the Requests inbox and Invoicing
  // both did. D5: the search spans every tab AND reaches settled orders,
  // because a search scoped to the tab you are on reproduces the same defect
  // one level down — you would have to already know which tab the order is on,
  // which is the thing you are searching to find out.
  const [search, setSearch] = useState('');
  const [settledRows, setSettledRows] = useState([]);
  const [settledLoading, setSettledLoading] = useState(false);
  const [settledCapped, setSettledCapped] = useState(false);
  const searching = search.trim().length > 0;

  // #304 D10, the undo bar. Gate callout M names the problem exactly: settling
  // the last line drops the order off this queue, and the ONLY thing on screen
  // saying so is a count going from 7 to 6 — a number quietly decreasing is not
  // feedback. The search above answers the CONSIDERED case, the vendor ringing
  // on Tuesday. This answers the SLIP, ten seconds ago, in one tap.
  //
  // An EXTRACTION rather than a build: RequestsInboxPage.js already had this
  // bar, with its undo action, its five-second timer and its own mobile
  // position. The gate priced it as droppable work and was wrong about that,
  // which its own D10 row now records.
  const { snackbar, showSnackbar, dismiss: dismissSnackbar } = useSnackbar();

  const [showMarkOrdered, setShowMarkOrdered] = useState(false);
  const [markOrderedReq, setMarkOrderedReq] = useState(null);
  const [moVendor, setMoVendor] = useState(null);
  const [moVendorSearch, setMoVendorSearch] = useState('');
  const [moVendorResults, setMoVendorResults] = useState([]);
  const [moPrice, setMoPrice] = useState('');
  const [moPackQty, setMoPackQty] = useState('1');
  const [moPackUnit, setMoPackUnit] = useState('');
  const [moPacksToOrder, setMoPacksToOrder] = useState('1');
  const [moPoRef, setMoPoRef] = useState('');
  // #287, 74y2. po_ref stops being hand-typed free text. moPoGenerated says the number
  // in moPoRef came from assign_po_number() and is therefore not editable - it is the
  // difference between tier 1 and the two manual tiers, and it is read from the live
  // setting rather than inferred from whether the string looks generated.
  const [moPoGenerated, setMoPoGenerated] = useState(false);
  const [moPoIssue, setMoPoIssue] = useState('');
  // The vendor's own order/confirmation number. Its own column by design - once Stede
  // owns po_ref there is nowhere else for this to live, and Engineering Standards
  // rule 19 exists to stop two identifiers sharing one column.
  const [moVendorOrderRef, setMoVendorOrderRef] = useState('');
  // 74p. Off-catalog buys had nowhere to record the vendor's own part number, which is
  // the identifier the vendor's order desk actually keys on - and with no catalog record
  // behind an off-catalog line there is nothing else to quote. Saved onto the provisional
  // vendor_items row, so promote_off_catalog_item()'s relink branch carries it into the
  // catalog entry #243's queue creates.
  const [moVendorItemNum, setMoVendorItemNum] = useState('');
  const [moEta, setMoEta] = useState('');

  // #395, Session 78n8.2 -- how the order reaches the vendor. Gate Screen 2.
  // 'send' is the default because D3 makes it the primary route: it is the only
  // one that can carry a formatted document and the PDF, and the draft route
  // fails silently on Outlook past about a thousand characters.
  const [moSendMode, setMoSendMode] = useState('send');
  const [moVendorNote, setMoVendorNote] = useState('');
  const [moSendPo, setMoSendPo] = useState(true);
  const [moSendConfirmation, setMoSendConfirmation] = useState(true);
  // Reported to the buyer AFTER the order is recorded, never thrown. A failed
  // send must not unwind a placed order -- same rule #351 established for the
  // vendor price write, and for the same reason: the order really was placed.
  const [moSendResult, setMoSendResult] = useState(null);
  const [moSaving, setMoSaving] = useState(false);
  const [moError, setMoError] = useState('');
  const [moDone, setMoDone] = useState(false);
  // #351, Session 78g. The vendor_items price loop discarded its errors, so a
  // failed write to the number the NEXT order and the cost basis both read was
  // completely silent. It must not throw - by the time it runs the order is
  // already placed, and unwinding a placed order over a price-cache write would
  // be worse than the silence. So it is surfaced as a warning ON the success
  // panel: the order stands, and the buyer is told which vendor prices did not
  // save so they can set them by hand in the vendor catalog.
  const [moPriceWarning, setMoPriceWarning] = useState(null);
  const [moItemState, setMoItemState] = useState({});

  const [showTracking, setShowTracking] = useState(false);
  const [trackingReq, setTrackingReq] = useState(null);
  const [trackCleared, setTrackCleared] = useState(false);
  const [trackingItem, setTrackingItem] = useState(null);
  const [trackCarrier, setTrackCarrier] = useState('');
  const [trackNumber, setTrackNumber] = useState('');
  const [trackEta, setTrackEta] = useState('');
  const [trackSaving, setTrackSaving] = useState(false);
  const [trackError, setTrackError] = useState('');
  const [trackDone, setTrackDone] = useState(false);

  // #288, Session 74z3 — receipt-close state. The request is held alongside the
  // line because the sheet's success message names the order when closing the
  // line settled it, and that is the only place a person learns the order they
  // were working on is now finished.
  const [shortCloseReq, setShortCloseReq] = useState(null);
  const [shortCloseItem, setShortCloseItem] = useState(null);
  const [shortCloseSaving, setShortCloseSaving] = useState(false);
  const [shortCloseError, setShortCloseError] = useState('');
  const [shortCloseDone, setShortCloseDone] = useState(false);
  const [shortCloseResult, setShortCloseResult] = useState(null);

  // Issue #19 — cancel state
  const [cancellingReq, setCancellingReq] = useState(null);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [cancelError, setCancelError] = useState('');

  // Issue #210 — purchase approval threshold state
  const [showApproval, setShowApproval] = useState(false);
  const [approvalReq, setApprovalReq] = useState(null);
  const [showDenyBox, setShowDenyBox] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [approvalSaving, setApprovalSaving] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const [approvalDone, setApprovalDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('material_requests')
        .select(REQUEST_SELECT)
        .in('status', ['approved', 'ordered', 'pending_purchase_approval'])
        .order('created_at', { ascending: true })
        .order('id', { foreignTable: 'material_request_items', ascending: true });

      if (err) throw err;
      setRequests(data || []);
    } catch (e) {
      setError('Failed to load queue: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // D5/D9. The second read, and it runs on the SERVER rather than filtering
  // rows already in hand. That is the whole difference between a lookup and a
  // filter: settled history grows forever, so loading "the newest 50 settled
  // orders" and filtering them client-side would fail on exactly the case this
  // exists for — the vendor ringing in November about an order from August.
  //
  // The status pair is what the queue deliberately does NOT load. `ordered`
  // rows are already here; a fully-received order becomes `received` and a
  // fully-settled one becomes `closed`, and both drop off this page by #284's
  // design, which is right and is not what #304 is about.
  // Bumped by anything that changes an order, so a search already on screen
  // re-runs against the new state. WITHOUT THIS the settled half is a snapshot
  // from whenever the term was last typed: settle an order while searching for
  // its PO and the live list correctly drops it while the settled list goes on
  // saying nothing matches — the two halves of one screen disagreeing, which is
  // the defect Colby caught in the empty state an hour earlier.
  const [dataVersion, setDataVersion] = useState(0);

  const fetchSettled = useCallback(async term => {
    // PostgREST's `or` takes a comma-separated list and treats `,` `(` `)` `.`
    // as structure, so a raw term could change the shape of the filter rather
    // than the value inside it. Every identifier this searches is alphanumeric
    // with dashes, slashes or spaces — DAL-PO-00046, SO-77812, 1Z999AA1012 —
    // so anything outside that class is dropped rather than escaped. A term
    // that is ENTIRELY punctuation returns nothing rather than an unbounded
    // query.
    const safe = term.replace(/[^A-Za-z0-9 _/-]/g, '').trim();
    if (!safe) return { rows: [], capped: false };
    const cols = ['request_number', 'po_ref', 'vendor_order_ref', 'work_order_ref', 'invoice_ref', 'tracking_number'];
    const filter = cols.map(c => `${c}.ilike."*${safe}*"`).join(',');
    const { data, error: err } = await supabase
      .from('material_requests')
      .select(REQUEST_SELECT)
      .in('status', ['received', 'closed'])
      .or(filter)
      // D11. No branch filter, deliberately, and it grants nothing new:
      // purchasing spans branches by design — Jennifer buys from Houston for
      // Dallas — so this page has never had one. The only thing widening is
      // WHICH STATUSES a search can reach, for people already looking at these
      // orders.
      .order('updated_at', { ascending: false })
      .limit(SETTLED_CAP + 1);
    if (err) throw err;
    const rows = data || [];
    return { rows: rows.slice(0, SETTLED_CAP), capped: rows.length > SETTLED_CAP };
  }, []);

  useEffect(() => {
    const term = search.trim();
    if (!term) {
      setSettledRows([]); setSettledCapped(false); setSettledLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSettledLoading(true);
    // Typing "DAL-PO-00046" is thirteen keystrokes and would otherwise be
    // thirteen queries against a table that only grows.
    const timer = setTimeout(async () => {
      try {
        const { rows, capped } = await fetchSettled(term);
        if (cancelled) return;
        setSettledRows(rows); setSettledCapped(capped);
      } catch (e) {
        // A failed settled lookup must not blank the live queue underneath it.
        // The live results are still correct and still on screen; what the
        // reader needs to know is that the settled half did not run, which is
        // the difference between "no settled order matches" and "we did not
        // look" — and those two look identical if this fails silently.
        if (!cancelled) { setSettledRows([]); setSettledCapped(false); setError('Could not search settled orders: ' + e.message); }
      } finally {
        if (!cancelled) setSettledLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search, fetchSettled, dataVersion]);

  useEffect(() => {
    async function loadSettings() {
      const { data } = await supabase.from('company_settings').select('*').single();
      setSettings(data || {});
    }
    loadSettings();
  }, []);

  // #395. Loaded once, beside the settings, for the same reason: both are small,
  // both change rarely, and the Mark as ordered sheet needs them the moment it
  // opens rather than after a fetch the buyer would watch.
  useEffect(() => {
    async function loadBranchAddresses() {
      const { data, error } = await supabase
        .from('branches')
        .select('id, name, street_address, address_line2, city, state, postal_code, phone, receiving_contact_name, uses_company_address');
      // Failing SOFT is correct here and is not laziness: the purchase order is
      // one control on a sheet whose main job is recording that an order was
      // placed. If this query fails, the Ship To resolves to nothing, the send
      // is BLOCKED with "this location has no address yet" -- which is honest --
      // and marking the order as placed still works.
      if (error) { console.error('[#395] branch address load failed', error); return; }
      setBranchRows(data || []);
    }
    loadBranchAddresses();
  }, []);

  // #256. This used to reseed every field from the preferred vendor's list price,
  // recompute packs from qty_requested, and blank the ETA - so a request that came
  // back from spend approval, or back from a denial, returned with numbers nobody had
  // agreed to and the buyer had to retype the order. The committed values are already
  // frozen on the row by submitMarkOrdered(): price_per_pack, packs_ordered and
  // tracking_eta for catalog items, and the provisional vendor_items record for
  // off-catalog. Restore from those whenever they exist, and fall back to the old
  // preferred-vendor seeding when they do not - a request being marked ordered for the
  // first time has nothing to restore and must still behave exactly as before.
  async function openMarkOrdered(req) {
    setMarkOrderedReq(req);
    setMoDone(false);
    setMoError('');
    setMoPoRef(req.po_ref || '');
    setMoVendorOrderRef(req.vendor_order_ref || '');
    setMoPoGenerated(false);
    setMoPoIssue('');
    setMoEta(toDateInputValue(req.tracking_eta));
    setMoVendorResults([]);
    setMoPackQty('1');
    setMoPacksToOrder('1');
    setMoVendorItemNum('');

    // #287, Option A at Colby's decision: the number is assigned when the sheet opens,
    // not when it is confirmed, because purchasing reads it to the vendor while placing
    // the order - a number that only appears after the call is a number nobody could
    // give out. The accepted cost is that opening and cancelling burns one. Gaps in a
    // PO sequence are ordinary and auditable; SAP number ranges have them.
    //
    // assign_po_number() returns the existing number rather than minting a second one
    // when the request already carries a po_ref, so reopening an ordered request is
    // safe and the guard does not need repeating here.
    if (settings?.po_number_generation_enabled !== false) {
      const { data: assigned, error: assignErr } = await supabase
        .rpc('assign_po_number', { p_request_id: req.id });
      if (assignErr) {
        setMoPoIssue('Could not assign a PO number automatically. Type one instead.');
      } else if (assigned) {
        setMoPoRef(assigned);
        setMoPoGenerated(true);
      }
    }

    const items = req.material_request_items || [];
    const item = items[0];
    const isOffCatalog = item?.is_off_catalog;

    if (isOffCatalog) {
      // #398. Was: setMoPackUnit(item.off_catalog_uom || 'each') - which took the
      // requester's STOCKING unit ("what is one of these?", Rec 20) and pre-filled
      // it as the answer to the PACKAGING question ("how does the vendor ship
      // it?", Rec 21). The screen then read "vendor sells by sheet, 50 units per
      // pack" - a sheet containing 50 sheets. That is the mechanism behind the
      // "units per each" nonsense #398 diagnosed as a symptom, and line 2332 wrote
      // the wrong-category value onto the vendor row, so it became permanent
      // catalog data the moment the item was promoted.
      //
      // Now it starts EMPTY and purchasing answers it. What the requester asked
      // for is still shown, as read-only context beside the field, so the
      // information is not lost - it just stops pretending to be her answer.
      setMoPackUnit('');
      setMoItemState({});
      const provisional = await fetchProvisionalVendorItem(item.id);
      if (provisional) {
        const unitPrice = provisional.vendor_price != null ? parseFloat(provisional.vendor_price) : null;
        const committedTotal = req.ordered_price != null ? parseFloat(req.ordered_price) : null;
        setMoPrice(toMoneyInput(unitPrice));
        setMoVendorItemNum(provisional.vendor_item_number || '');
        setMoPackQty(provisional.pack_qty != null ? String(provisional.pack_qty) : '1');
        if (provisional.pack_unit) setMoPackUnit(provisional.pack_unit);
        setMoPacksToOrder(
          unitPrice > 0 && committedTotal != null
            ? String(Math.round(committedTotal / unitPrice) || 1)
            : '1'
        );
      } else {
        setMoPrice('');
      }
      const restoredVendor = provisional?.vendor_id ? await fetchVendorById(provisional.vendor_id) : null;
      setMoVendor(restoredVendor);
      setMoVendorSearch(restoredVendor?.name || item.off_catalog_vendor_name || '');
    } else {
      setMoPackUnit('');
      const initialState = {};
      const preferredVendorIds = new Set();
      const preferredVendorById = {};
      let committedVendor = null;
      items.forEach(it => {
        const inventoryItem = it.inventory_items;
        const preferred = inventoryItem?.vendor_items?.find(vi => vi.is_preferred);
        const preferredPackQty = preferred?.pack_qty > 0 ? preferred.pack_qty : 1;
        const hasCommitted = it.price_per_pack != null || it.packs_ordered != null;
        initialState[it.id] = {
          checked: true,
          price: it.price_per_pack != null
            ? toMoneyInput(it.price_per_pack)
            : toMoneyInput(preferred?.vendor_price),
          packs: it.packs_ordered != null
            ? String(it.packs_ordered)
            : String(Math.ceil((it.qty_requested || 0) / preferredPackQty) || 1),
        };
        if (hasCommitted && req.vendor_id) {
          const match = inventoryItem?.vendor_items?.find(vi => vi.vendor_id === req.vendor_id);
          if (match?.inventory_vendors) committedVendor = match.inventory_vendors;
        }
        if (preferred?.inventory_vendors) {
          preferredVendorIds.add(preferred.inventory_vendors.id);
          preferredVendorById[preferred.inventory_vendors.id] = preferred.inventory_vendors;
        }
      });
      setMoItemState(initialState);
      setMoPrice('');

      // Live-test finding, 74p. committedVendor resolves through the item's vendor_items
      // relation, which only exists if that vendor was ever associated with that catalog
      // item. A buyer can pick any vendor at Mark Ordered without creating an
      // association, and DAL-REQ-00043 on staging did exactly that - so the restore
      // brought back price, packs, PO ref and ETA but left the vendor field empty. The row
      // itself knows the answer: material_requests.vendor_id.
      const hasCommittedAnywhere = items.some(it => it.price_per_pack != null || it.packs_ordered != null);
      if (!committedVendor && hasCommittedAnywhere && req.vendor_id) {
        committedVendor = await fetchVendorById(req.vendor_id);
      }

      if (committedVendor) {
        setMoVendor(committedVendor);
        setMoVendorSearch(committedVendor.name);
      } else if (preferredVendorIds.size === 1) {
        const onlyVendor = preferredVendorById[[...preferredVendorIds][0]];
        setMoVendor(onlyVendor);
        setMoVendorSearch(onlyVendor.name);
      } else {
        setMoVendor(null);
        setMoVendorSearch('');
      }
    }
    setShowMarkOrdered(true);
  }

  function toggleMoItemChecked(itemId) {
    setMoItemState(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], checked: !prev[itemId]?.checked },
    }));
  }

  function setMoItemPrice(itemId, value) {
    setMoItemState(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], price: value },
    }));
  }

  function setMoItemPacks(itemId, value) {
    setMoItemState(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], packs: value },
    }));
  }

  useEffect(() => {
    if (moVendor || !markOrderedReq) return;
    const items = markOrderedReq.material_request_items || [];
    if (items[0]?.is_off_catalog) return;
    const checkedItems = items.filter(it => moItemState[it.id]?.checked);
    if (checkedItems.length === 0) return;
    const vendorIds = new Set();
    const vendorById = {};
    checkedItems.forEach(it => {
      const preferred = it.inventory_items?.vendor_items?.find(vi => vi.is_preferred);
      if (preferred?.inventory_vendors) {
        vendorIds.add(preferred.inventory_vendors.id);
        vendorById[preferred.inventory_vendors.id] = preferred.inventory_vendors;
      }
    });
    if (vendorIds.size === 1) {
      const v = vendorById[[...vendorIds][0]];
      setMoVendor(v);
      setMoVendorSearch(v.name);
    }
  }, [moItemState, moVendor, markOrderedReq]);

  function openTracking(req, item) {
    const target = item || req.material_request_items?.[0] || null;
    setTrackingReq(req);
    setTrackingItem(target);
    setTrackCarrier(target?.tracking_carrier || '');
    setTrackNumber(target?.tracking_number || '');
    setTrackEta(target?.tracking_eta ? target.tracking_eta.split('T')[0] : '');
    setTrackDone(false);
    setTrackCleared(false);
    setTrackError('');
    setShowTracking(true);
  }

  // #287. Uniqueness is an invariant rather than a tier - it holds in all three modes,
  // including free text. The partial unique index is what actually guarantees it; this
  // check exists so the collision can name the order it hit instead of surfacing a raw
  // 23505 the buyer cannot act on. A bare rejection tells Jennifer she is wrong; this
  // tells her where to go and look. Same instrument #290 specifies for duplicate
  // vendor invoices.
  async function validatePoNumber(requestId) {
    const val = (moPoRef || '').trim();
    if (!val) return true;

    if (!moPoGenerated
        && settings?.po_number_format_enforced
        && settings?.po_number_format
        && !poNumberMatchesFormat(val, settings.po_number_format)) {
      setMoPoIssue(`Needs to look like ${settings.po_number_format}.`);
      return false;
    }

    const { data: clash, error: clashErr } = await supabase
      .from('material_requests')
      .select('id, request_number, ordered_at, vendor_id')
      .eq('po_ref', val)
      .neq('id', requestId)
      .limit(1);

    // A failed lookup must not block the save. The unique index still stands behind
    // this, so the worst case is a raw constraint error instead of a friendly one -
    // never a duplicate slipping through.
    if (clashErr) return true;
    if (!clash || clash.length === 0) return true;

    const hit = clash[0];
    let vendorName = '';
    if (hit.vendor_id) {
      const { data: v } = await supabase
        .from('inventory_vendors').select('name').eq('id', hit.vendor_id).maybeSingle();
      vendorName = v?.name ? ` from ${v.name}` : '';
    }
    const when = hit.ordered_at
      ? `, ordered ${new Date(hit.ordered_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
      : '';
    setMoPoIssue(`Already used on ${hit.request_number}${when}${vendorName}. Pick a different number.`);
    return false;
  }

  // #395, Session 78n8.2. ONE delivery path, used by both branches of
  // submitMarkOrdered.
  //
  // Extracted rather than copied because the duplication would be in the
  // SIDE-EFFECT sequence -- build the document, send it to the vendor, confirm
  // the requester -- which is precisely the case CLAUDE.md's rule 14 names.
  //
  // THE LIVE TEST AT 78n8.2 IS WHY THIS EXISTS. The delivery was first written
  // inline in the on-catalog branch only, and submitMarkOrdered has a SEPARATE
  // off-catalog branch that returns early. So an off-catalog order drew the
  // whole Send the order panel, let the buyer pick a route and type a note to
  // the vendor, and then sent nothing at all -- while the sheet still reported
  // "Order placed". Invisible from the code and obvious the moment a real
  // off-catalog request went through it.
  async function runPurchaseOrderDelivery({ requestId, requestNumber, workOrderRef, lines, managerEmails = [] }) {
    const branch = (branchRows || []).find(b => b.id === markOrderedReq.branch_id) || null;

    // The same derivation MarkOrderedSheet uses for the Confirm button's label.
    // A blocked send is a "don't send" whatever the pills happen to show; if it
    // were attempted anyway it would degrade rather than break, but it would
    // report "the order could not be emailed" as a failure when the truth is
    // that nobody ever asked for it to be sent.
    const blocked = purchaseOrderBlockers({ vendor: moVendor, branch, settings, buyer: profile }).length > 0;
    const effectiveMode = blocked && moSendMode === 'send' ? 'none' : moSendMode;

    const delivery = await deliverPurchaseOrder({
      order: buildPurchaseOrder({
        poRef: moPoRef,
        requestNumber,
        workOrderRef,
        vendor: moVendor,
        branch,
        settings,
        buyer: profile,
        lines,
        note: moVendorNote,
        orderedAt: new Date().toISOString(),
      }),
      mode: effectiveMode,
      sendPo: moSendPo,
      sendConfirmation: moSendConfirmation,
      requesterId: markOrderedReq.requested_by,
      requestId,
      requestNumber,
      buyer: profile,
      // #413, Session 78n8.3. The approvers' in-app notification resolves from
      // these two through the same #396 lookup the email above uses, so the bell
      // and the email cannot address different people for one event.
      branchId: markOrderedReq.branch_id,
      departmentId: markOrderedReq.department_id,
      managerEmails,
    });
    setMoSendResult(delivery);

    // Opened from here rather than inside the delivery module, because
    // navigating to a mailto: is a browser action and that module stays
    // testable without one.
    if (delivery.mailtoHref) window.location.href = delivery.mailtoHref;
  }

  async function submitMarkOrdered() {
    if (!markOrderedReq) return;
    if (!moVendor) { setMoError('Select a vendor before confirming.'); return; }
    if (!(await validatePoNumber(markOrderedReq.id))) return;

    const allItems = markOrderedReq.material_request_items || [];
    const firstItem = allItems[0];
    const isOffCatalog = firstItem?.is_off_catalog;

    // #398. The sells-by field is no longer pre-filled from the requester's
    // stocking unit, so it can legitimately be empty - which means it has to be
    // ASKED for rather than assumed. Only when a price is being recorded: an
    // order captured without a price has nothing to divide, so nothing to get
    // wrong. Guarded here rather than only in the database, so the person sees a
    // sentence instead of a constraint violation.
    if (isOffCatalog && moPrice && !moPackUnit) {
      setMoError('Say what the vendor sells this by before confirming - it is what the price is per.');
      return;
    }

    if (isOffCatalog) {
      setMoSaving(true);
      setMoError('');
      try {
        const item = firstItem;
        const orderTotal = moPrice ? parseFloat(moPrice) * (parseFloat(moPacksToOrder) || 1) : null;
        const thresholdAmount = parseFloat(settings?.purchase_approval_threshold_amount) || 0;
        const overThreshold = needsSpendApproval(settings, orderTotal, markOrderedReq.approved_amount);

        // #350, Session 79.14. Three writes became one call.
        //
        // This used to be: update the header, then update the line's price, then
        // insert the provisional vendor row - three round trips with nothing
        // holding them together. mark_material_request_ordered() does all three
        // inside one transaction, so an off-catalog order either lands complete
        // or does not land at all.
        //
        // #283 still holds and is now enforced in the function rather than here:
        // the price goes onto the LINE as well as the header, written as
        // price_per_pack and packs_ordered because ordered_price is GENERATED
        // ALWAYS AS the product of those two. Partial invoicing compares against
        // the ordered price of the lines being invoiced, so a price that lives
        // only on the header cannot be compared exactly once a request holds
        // more than one line.
        //
        // p_lines carries the price keys ONLY when a price was typed. That is
        // load-bearing rather than tidy: the function leaves a line alone when
        // neither key is present, which preserves this path's original
        // `if (moPrice)` guard. Sending them as nulls would wipe what the vendor
        // charged last time on a re-order where the box was left empty.
        const packQtyNum = parseInt(moPackQty) || 1;
        const { data: moResult, error: rpcErr } = await supabase.rpc('mark_material_request_ordered', {
          p_request_id: markOrderedReq.id,
          p_vendor_id: moVendor.id,
          p_po_ref: moPoRef || null,
          p_vendor_order_ref: moVendorOrderRef.trim() || null,
          p_tracking_eta: moEta ? new Date(moEta + 'T12:00:00').toISOString() : null,
          p_over_threshold: overThreshold,
          p_threshold_amount: overThreshold ? thresholdAmount : null,
          p_lines: [
            moPrice
              ? {
                  request_item_id: item.id,
                  price_per_pack: parseFloat(moPrice),
                  packs_ordered: parseFloat(moPacksToOrder) || 1,
                }
              : { request_item_id: item.id },
          ],
          p_provisional: moPrice
            ? {
                provisional_request_item_id: item.id,
                vendor_price: parseFloat(moPrice) || 0,
                pack_qty: packQtyNum,
                // NULL, never ''. Both columns carry a foreign key into
                // units_of_measure(name), and an empty string is not a unit - it
                // would be rejected outright rather than stored as "unknown".
                pack_unit: moPackUnit || null,
                off_catalog_description: item.off_catalog_description || null,
                off_catalog_pack_qty: packQtyNum,
                off_catalog_pack_unit: moPackUnit || null,
                vendor_item_number: moVendorItemNum.trim() || null,
              }
            : null,
        });
        if (rpcErr) throw rpcErr;

        // The provisional vendor row is deliberately non-fatal inside the
        // function - it is a price note for an item that is not in the catalog,
        // and it must not unwind an order already placed with the vendor. It was
        // previously inserted with NO error check at all, so a failure was
        // invisible; now it comes back and is said out loud.
        setMoPriceWarning(moResult?.provisional_written === false
          ? [item.off_catalog_description || 'this item']
          : null);

        const branchLabel = BRANCHES.find(b => b.id === markOrderedReq.branch_id)?.name || '';
        const itemName = item.off_catalog_description || markOrderedReq.request_number;

        if (overThreshold) {
          const { data: execUsers } = await supabase
            .from('users')
            .select('id')
            .in('role', ['admin', 'executive'])
            .eq('is_active', true);
          const execIds = (execUsers || []).map(u => u.id);
          if (execIds.length > 0) {
            await sendNotification(
              execIds,
              'Purchase order needs your approval',
              `${itemName} \u2014 ${markOrderedReq.request_number}: $${orderTotal.toFixed(2)} exceeds the $${thresholdAmount.toFixed(2)} threshold.`,
              'purchase_approval_needed', 'purchasing', 'purchasing_queue', markOrderedReq.id
            );
          }
          if (markOrderedReq.requested_by) {
            await sendNotification(
              [markOrderedReq.requested_by],
              'Your request is awaiting approval',
              `${itemName} \u2014 ${markOrderedReq.request_number} exceeds the purchase approval threshold and needs executive sign-off before it's ordered.`,
              'purchase_approval_pending', 'purchasing', null, markOrderedReq.id
            );
          }
          await notifyPurchaseApprovalNeeded({
            branchName: branchLabel,
            requestNumber: markOrderedReq.request_number,
            itemName,
            orderTotal,
            thresholdAmount,
            requestId: markOrderedReq.id,
          });
        } else {
          const managerEmails = await notifyRequestOrdered({
            branchId: markOrderedReq.branch_id,
            // #413, Session 78n8.3. Required for the approver scoping -- without
            // it a department-scoped manager who CAN approve this request is
            // silently dropped from the email. Omitting it fails safe rather
            // than silently, but it fails.
            departmentId: markOrderedReq.department_id,
            branchName: branchLabel,
            requestNumber: markOrderedReq.request_number,
            itemName,
            vendorName: moVendor.name,
            poRef: moPoRef,
            eta: moEta,
            orderedByName: profile?.full_name,
            requestId: markOrderedReq.id,
          }) || [];

          // #395. The half this branch was missing entirely until the live test
          // at 78n8.2. An off-catalog order is still an order: it gets the same
          // document, the same routes and the same requester confirmation.
          //
          // ONE line, built from the off-catalog fields rather than from
          // material_request_items -- the description IS the item name here, and
          // `moPackUnit` is what the vendor sells it by, which is the whole
          // point of #398 asking for it rather than inheriting the requester's
          // stocking unit.
          await runPurchaseOrderDelivery({
            requestId: markOrderedReq.id,
            requestNumber: markOrderedReq.request_number,
            workOrderRef: markOrderedReq.work_order_ref,
            lines: [{
              name: item.off_catalog_description || itemName,
              sku: '',
              qty: parseFloat(moPacksToOrder) || 1,
              unit: moPackUnit || item.off_catalog_uom || '',
              pricePerPack: parseFloat(moPrice) || 0,
            }],
            managerEmails,
          });
        }

        setMoDone(true);
        setSelectedReq(null);
        await load();
      } catch (e) {
        setMoError('Something went wrong: ' + e.message);
      } finally {
        setMoSaving(false);
      }
      return;
    }

    const checkedItems = allItems.filter(it => moItemState[it.id]?.checked);
    if (checkedItems.length === 0) { setMoError('Select at least one item to order.'); return; }

    // #350 RESOLVED, Session 79.14. The mitigation that used to sit here is gone
    // along with the failure it described, and this note replaces it rather than
    // being appended to it - the old comment told a reader the split was six
    // untransacted writes, and leaving that standing would be a lie about the
    // code directly beneath it.
    //
    // WHAT THE MITIGATION WAS FOR, so nobody reintroduces the hole: the split
    // used to be five client-side writes with no transaction, and a failure
    // partway left the ticked lines on a child request that had no vendor and no
    // PO while the parent had silently lost them. Two recovery states had to be
    // tracked and named in the error message because they needed OPPOSITE fixes
    // - an empty child is cancelled, a filled child must be finished rather than
    // retried - and pressing Mark Ordered again in the second state created a
    // SECOND child.
    //
    // None of that can happen now. mark_material_request_ordered() does the whole
    // sequence in one transaction and takes a row lock on the parent, so there is
    // no half-finished state to describe, no second child to warn about, and
    // nothing for the buyer to recover by hand.
    setMoSaving(true);
    setMoError('');
    setMoPriceWarning(null); // #351 — never carry a previous order's warning forward.
    try {
      const orderTotal = checkedItems.reduce((sum, it) => {
        const st = moItemState[it.id];
        const price = parseFloat(st?.price) || 0;
        const packs = parseFloat(st?.packs) || 0;
        return sum + price * packs;
      }, 0);
      const thresholdAmount = parseFloat(settings?.purchase_approval_threshold_amount) || 0;
      const overThreshold = needsSpendApproval(settings, orderTotal, markOrderedReq.approved_amount);

      // #395. The purchase order's lines, mapped from exactly the state
      // orderTotal was summed from a few lines above -- so the total printed on
      // the document the vendor receives cannot disagree with the total this
      // function just used to decide whether the order needs approval.
      const poDocLines = checkedItems.map(it => {
        const st = moItemState[it.id];
        return {
          name: it.inventory_items?.name || itemLabel(it),
          sku: it.inventory_items?.sku || '',
          qty: parseFloat(st?.packs) || 0,
          unit: itemUom(it) || '',
          pricePerPack: parseFloat(st?.price) || 0,
        };
      });

      const parseNumOrNull = v => {
        const n = parseFloat(v);
        return Number.isNaN(n) ? null : n;
      };

      // #350, Session 79.14. THIS IS THE WHOLE FIX.
      //
      // What used to be here: create the child request, move the ticked lines
      // onto it, loop writing each line's price, update the header, then loop
      // again writing the vendor price cache. Five steps, no transaction. A
      // failure at step three left the lines on a child request with no vendor
      // and no PO while the parent had silently lost them, and pressing the
      // button again made a SECOND child.
      //
      // It is now one call. Either all of it happens or none of it does, and the
      // function takes a row lock on the parent so two presses serialise instead
      // of both deciding to split.
      //
      // THE SPLIT IS NOT PASSED IN - the function derives it by comparing the
      // lines being ordered against the lines on the request. That is deliberate:
      // it cannot be told "this is not a split" while lines are left behind,
      // which would mark a request ordered that still holds un-ordered lines.
      //
      // vendor_item_id is resolved HERE because only the screen has the embedded
      // vendor_items rows, and the price_locked check is repeated inside the
      // function so a stale screen cannot overwrite a locked price.
      const rpcLines = checkedItems.map(it => {
        const st = moItemState[it.id];
        const matching = it.inventory_items?.vendor_items?.find(vi => vi.vendor_id === moVendor.id);
        return {
          request_item_id: it.id,
          price_per_pack: parseNumOrNull(st?.price),
          packs_ordered: parseNumOrNull(st?.packs),
          vendor_item_id: matching && !matching.price_locked ? matching.id : null,
        };
      });

      const { data: moResult, error: rpcErr } = await supabase.rpc('mark_material_request_ordered', {
        p_request_id: markOrderedReq.id,
        p_vendor_id: moVendor.id,
        p_po_ref: moPoRef || null,
        p_vendor_order_ref: moVendorOrderRef.trim() || null,
        p_tracking_eta: moEta ? new Date(moEta + 'T12:00:00').toISOString() : null,
        p_over_threshold: overThreshold,
        p_threshold_amount: overThreshold ? thresholdAmount : null,
        p_lines: rpcLines,
        p_provisional: null,
      });
      if (rpcErr) throw rpcErr;

      // The order is placed. Everything below is telling people about it, and
      // none of it may unwind the order.
      const targetRequestId = moResult.target_request_id;
      const targetRequestNumber = moResult.target_request_number;

      // #351, Session 78g, preserved through the rewrite. A failed vendor price
      // write is REPORTED and never thrown - the price cache is a convenience and
      // an order that reached the vendor must not be rolled back over it. The
      // function returns the request_item_ids it could not write, and they are
      // mapped back to names here because only the screen knows the names.
      const failedIds = moResult.price_write_failures || [];
      const priceWriteFailures = failedIds
        .map(id => {
          const hit = checkedItems.find(it => String(it.id) === String(id));
          return hit?.inventory_items?.name || `item ${id}`;
        });
      if (priceWriteFailures.length) {
        console.error('[#351] vendor_items price write failed', { failedIds, requestId: targetRequestId });
      }
      setMoPriceWarning(priceWriteFailures.length ? priceWriteFailures : null);

      const branchLabel = BRANCHES.find(b => b.id === markOrderedReq.branch_id)?.name || '';
      const itemNameSummary = checkedItems.length === 1
        ? (checkedItems[0].inventory_items?.name || '')
        : checkedItems.map(it => it.inventory_items?.name).filter(Boolean).join(', ');

      if (overThreshold) {
        const { data: execUsers } = await supabase
          .from('users')
          .select('id')
          .in('role', ['admin', 'executive'])
          .eq('is_active', true);
        const execIds = (execUsers || []).map(u => u.id);
        if (execIds.length > 0) {
          await sendNotification(
            execIds,
            'Purchase order needs your approval',
            `${itemNameSummary} \u2014 ${targetRequestNumber}: $${orderTotal.toFixed(2)} exceeds the $${thresholdAmount.toFixed(2)} threshold.`,
            'purchase_approval_needed', 'purchasing', 'purchasing_queue', targetRequestId
          );
        }
        if (markOrderedReq.requested_by) {
          await sendNotification(
            [markOrderedReq.requested_by],
            'Your request is awaiting approval',
            `${itemNameSummary} \u2014 ${targetRequestNumber} exceeds the purchase approval threshold and needs executive sign-off before it's ordered.`,
            'purchase_approval_pending', 'purchasing', null, targetRequestId
          );
        }
        await notifyPurchaseApprovalNeeded({
          branchName: branchLabel,
          requestNumber: targetRequestNumber,
          itemName: itemNameSummary,
          orderTotal,
          thresholdAmount,
          // The CHILD request when a line was unticked, not the parent. Same id the
          // sendNotification() above uses, and the reason #350's mitigation names
          // the child by number: that is the record the reader has to act on.
          requestId: targetRequestId,
        });
      } else {
        const managerEmails = await notifyRequestOrdered({
          branchId: markOrderedReq.branch_id,
          // #413, Session 78n8.3 -- see the sibling call in the off-catalog
          // branch above. Both branches, deliberately: 78n8.2's live test found
          // that a change made to only one of them looks complete in the code
          // and does nothing for half the orders.
          departmentId: markOrderedReq.department_id,
          branchName: branchLabel,
          requestNumber: targetRequestNumber,
          itemName: itemNameSummary,
          vendorName: moVendor.name,
          poRef: moPoRef,
          eta: moEta,
          orderedByName: profile?.full_name,
          requestId: targetRequestId,
        }) || [];

        // #395, Session 78n8.2 -- gate Screen 2. The vendor's order and the
        // requester's confirmation, in the same action.
        //
        // DELIBERATELY INSIDE THE `else`, and this is the correctness decision
        // in this build. When the order is over the dollar approval threshold
        // its status becomes `pending_purchase_approval`, NOT `ordered` -- it is
        // not an order yet, it is a request for sign-off. Emailing a purchase
        // order to a vendor at that moment would commit D&R to spend that
        // nobody has approved, and it would be irretrievable: the vendor has
        // the order and Stede's own record says it was never placed.
        await runPurchaseOrderDelivery({
          requestId: targetRequestId,
          requestNumber: targetRequestNumber,
          workOrderRef: markOrderedReq.work_order_ref,
          lines: poDocLines,
          managerEmails,
        });
      }

      setMoDone(true);
      setSelectedReq(null);
      await load();
    } catch (e) {
      // #350, Session 79.14. The two split-recovery messages that used to live
      // here are gone because the states they described cannot occur any more.
      //
      // THE MESSAGE NOW TELLS THE BUYER THE ONE THING THAT IS ACTUALLY TRUE AND
      // ACTIONABLE: nothing was saved, so trying again is safe. That sentence
      // was the single most useful part of the old mitigation and it was the one
      // case the old code could not say with confidence - it had to hedge,
      // because it genuinely did not know how far the writes had got.
      setMoError(
        `Nothing was saved, so it is safe to try again: ${e.message}. `
        + `The order is written in one piece — if any part of it fails, none of it is kept, `
        + `so this request has not been split and no order has been placed.`
      );
    } finally {
      setMoSaving(false);
    }
  }

  function openApproval(req) {
    setApprovalReq(req);
    setShowDenyBox(false);
    setDenyReason('');
    setApprovalError('');
    setApprovalDone(false);
    setShowApproval(true);
  }

  async function submitApprovePurchase() {
    if (!approvalReq) return;
    setApprovalSaving(true);
    setApprovalError('');
    try {
      // #256. Was status 'ordered' plus notifyRequestOrdered(), which told the branch an
      // order had been placed that nobody had placed - Stede transmits nothing to a vendor.
      // Approval now returns the request to Needs Action and tells the people who can act
      // on Mark Ordered. The branch still hears "Order placed", from submitMarkOrdered(),
      // when someone actually places it.
      const approvedAmount = approvalReq.ordered_price != null ? parseFloat(approvalReq.ordered_price) : null;
      const { error: updErr } = await supabase
        .from('material_requests')
        .update({
          status: 'approved',
          approved_by: profile.id,
          approved_at: new Date().toISOString(),
          approved_amount: approvedAmount,
          denial_reason: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', approvalReq.id);
      if (updErr) throw updErr;

      const item = approvalReq.material_request_items?.[0];
      const isOffCatalog = item?.is_off_catalog;
      const branchLabel = BRANCHES.find(b => b.id === approvalReq.branch_id)?.name || '';
      const itemName = isOffCatalog
        ? (item.off_catalog_description || approvalReq.request_number)
        : (item?.inventory_items?.name || '');

      const { data: actionUsers } = await supabase
        .from('users')
        .select('id')
        .in('role', ['admin', 'executive', 'purchasing'])
        .eq('is_active', true);
      const actionIds = (actionUsers || []).map(u => u.id);
      if (actionIds.length > 0) {
        await sendNotification(
          actionIds,
          'Approved - ready to place the order',
          `${itemName} — ${approvalReq.request_number}${approvedAmount != null ? ': $' + approvedAmount.toFixed(2) : ''} was approved. Place the order with the vendor, then mark it ordered.`,
          'purchase_approval_granted', 'purchasing', 'purchasing_queue', approvalReq.id
        );
      }
      await notifyPurchaseApprovalGranted({
        branchName: branchLabel,
        requestNumber: approvalReq.request_number,
        itemName,
        approvedAmount,
        approverName: profile?.full_name,
        requestId: approvalReq.id,
      });

      setApprovalDone(true);
      setSelectedReq(null);
      await load();
    } catch (e) {
      setApprovalError('Something went wrong: ' + e.message);
    } finally {
      setApprovalSaving(false);
    }
  }

  async function submitDenyPurchase() {
    if (!approvalReq) return;
    setApprovalSaving(true);
    setApprovalError('');
    try {
      const { error: updErr } = await supabase
        .from('material_requests')
        .update({
          status: 'approved',
          denial_reason: denyReason || null,
          approved_by: null,
          approved_at: null,
          approved_amount: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', approvalReq.id);
      if (updErr) throw updErr;

      const { data: actionUsers } = await supabase
        .from('users')
        .select('id')
        .in('role', ['admin', 'executive', 'purchasing'])
        .eq('is_active', true);
      const actionIds = (actionUsers || []).map(u => u.id);
      const item = approvalReq.material_request_items?.[0];
      const itemName = item?.is_off_catalog
        ? (item.off_catalog_description || approvalReq.request_number)
        : (item?.inventory_items?.name || '');
      const branchLabel = BRANCHES.find(b => b.id === approvalReq.branch_id)?.name || '';

      if (actionIds.length > 0) {
        await sendNotification(
          actionIds,
          'Purchase order denied',
          `${itemName} \u2014 ${approvalReq.request_number} was denied${denyReason ? ': ' + denyReason : ''}. Revise and resubmit through Mark Ordered.`,
          'purchase_approval_denied', 'purchasing', 'purchasing_queue', approvalReq.id
        );
      }
      if (approvalReq.requested_by) {
        await sendNotification(
          [approvalReq.requested_by],
          'Your request needs another look',
          `${itemName} \u2014 ${approvalReq.request_number} was sent back by purchasing${denyReason ? ': ' + denyReason : ''}.`,
          'purchase_approval_denied', 'purchasing', null, approvalReq.id
        );
      }
      await notifyPurchaseApprovalDenied({
        branchName: branchLabel,
        requestNumber: approvalReq.request_number,
        itemName,
        reason: denyReason,
        requestId: approvalReq.id,
      });

      setShowApproval(false);
      setSelectedReq(null);
      await load();
    } catch (e) {
      setApprovalError('Something went wrong: ' + e.message);
    } finally {
      setApprovalSaving(false);
    }
  }

  // #410, Session 78n8. The vendor's own order number, recorded when it actually
  // arrives rather than at the moment of ordering, when nobody has it yet.
  //
  // Returns an error STRING rather than throwing or holding its own error state,
  // so the inline row that calls it owns its own message and this page does not
  // grow a seventh piece of sheet state for one text box.
  //
  // Single table, single row, so a plain update -- locked rule 7 governs writes
  // that must span tables together.
  //
  // A cleared box stores NULL, not '', because "" would be a confirmation number
  // that exists and is blank, and isAwaitingConfirmation() would then report the
  // order as confirmed while showing nothing. Same reasoning as #403's addresses.
  async function saveVendorOrderRef(req, value) {
    const next = String(value || '').trim() || null;
    if (next === (req.vendor_order_ref || null)) return null;
    try {
      const { error: updErr } = await supabase
        .from('material_requests')
        .update({ vendor_order_ref: next, updated_at: new Date().toISOString() })
        .eq('id', req.id);
      if (updErr) throw updErr;
      await load();
      return null;
    } catch (e) {
      return 'Could not save that. ' + e.message;
    }
  }

  async function submitClearTracking() {
    if (!trackingReq || !trackingItem) return;
    setTrackSaving(true);
    setTrackError('');
    try {
      const { error: updErr } = await supabase
        .from('material_request_items')
        .update({ tracking_carrier: null, tracking_number: null, tracking_eta: null, updated_at: new Date().toISOString() })
        .eq('id', trackingItem.id);
      if (updErr) throw updErr;

      setTrackCleared(true);
      setTrackDone(true);
      await load();
    } catch (e) {
      setTrackError('Something went wrong: ' + e.message);
    } finally {
      setTrackSaving(false);
    }
  }

  async function submitTracking() {
    if (!trackingReq) return;
    if (!trackingItem) { setTrackError('No item selected for tracking.'); return; }
    if (!trackNumber.trim()) { setTrackError('Enter a tracking number.'); return; }
    setTrackSaving(true);
    setTrackError('');
    try {
      const updates = {
        tracking_carrier: trackCarrier || null,
        tracking_number: trackNumber.trim(),
        tracking_eta: trackEta ? new Date(trackEta + 'T12:00:00').toISOString() : null,
        updated_at: new Date().toISOString(),
      };

      const { error: updErr } = await supabase
        .from('material_request_items')
        .update(updates)
        .eq('id', trackingItem.id);
      if (updErr) throw updErr;

      setTrackDone(true);
      await load();
    } catch (e) {
      setTrackError('Something went wrong: ' + e.message);
    } finally {
      setTrackSaving(false);
    }
  }

  // #288, Session 74z3. Receipt-close and its reversal.
  //
  // Both go through set_material_request_item_close_flag() rather than a
  // client-side .update(), because three tables move together — the flag on the
  // line, the audit row, and the request's status — which is locked rule 7. The
  // status half is the part that matters: the settle rule that decides whether
  // an order is finished lives in one Postgres function now, shared with
  // enter_material_request_invoice(), so the two paths cannot disagree about
  // when an order is done.
  function openShortClose(req, item) {
    setShortCloseReq(req);
    setShortCloseItem(item);
    setShortCloseError('');
    setShortCloseDone(false);
    setShortCloseResult(null);
  }

  // #304 D10. THE UNDO BAR IS RAISED HERE RATHER THAN IN submitShortClose, AND
  // THE LIVE TEST IS WHY. Fired at the moment the write lands, it appears
  // UNDERNEATH the sheet's own "Line closed" confirmation, which the person is
  // still looking at — and its five-second timer runs out while they read that
  // and reach for Done. It was built correctly and placed at a moment nobody
  // could see it, which is the same class as a control that reports nothing.
  //
  // Raising it as the sheet closes puts it on screen at the instant the queue
  // is revealed with the order gone from it, which is exactly when "where did
  // it go" gets asked.
  function closeShortCloseSheet() {
    const settled = shortCloseResult?.request_closed;
    const closedItem = shortCloseItem;
    const label = shortCloseReq?.request_number || 'The order';
    setShortCloseReq(null);
    setShortCloseItem(null);
    setShortCloseError('');
    setShortCloseDone(false);
    setShortCloseResult(null);
    // Only when the order actually LEFT. Closing one line of three changes
    // nothing a person cannot still see, and a bar on every close would be
    // noise on the surface Purchasing works all day.
    if (settled && closedItem) {
      showSnackbar(
        `${label} settled and left the queue`,
        () => reopenLineForReceiving(closedItem),
      );
    }
  }

  async function submitShortClose(reason) {
    if (!shortCloseItem) return;
    setShortCloseSaving(true);
    setShortCloseError('');
    try {
      const result = await setLineCloseFlag({
        itemId: shortCloseItem.id,
        userId: profile.id,
        flag: RECEIPT,
        closed: true,
        reason,
      });
      setShortCloseResult(result);
      setShortCloseDone(true);
      // Settling the last line closes the request, and 'closed' is not in
      // load()'s status filter — so the order leaves this queue entirely. The
      // selection has to be cleared or the detail panel keeps rendering an
      // order that is no longer in the list beside it, which reads as a filter
      // that failed. Same reason submitCancel clears it.
      if (result?.request_closed) {
        setSelectedReq(null);
        setMobileDetailReq(null);
      }
      await load();
      setDataVersion(v => v + 1);
    } catch (e) {
      setShortCloseError('Could not close the line: ' + e.message);
    } finally {
      setShortCloseSaving(false);
    }
  }

  // Reversal takes no reason and no sheet — one press, as drawn at 74z3's gate.
  // What makes that safe is that the reversal writes its own audit row, so an
  // undo is as visible afterwards as the close it undid.
  //
  // Failure reports through the page-level error, which renders in the list
  // column. On mobile the detail sheet covers it, so a failed reopen there is
  // quieter than it should be — noted rather than hidden, and worth its own row
  // if it ever bites.
  async function reopenLineForReceiving(item) {
    try {
      await setLineCloseFlag({
        itemId: item.id,
        userId: profile.id,
        flag: RECEIPT,
        closed: false,
        reason: null,
      });
      await load();
      setDataVersion(v => v + 1);
    } catch (e) {
      setError('Could not reopen the line: ' + e.message);
    }
  }

  // Issue #19 — real cancel logic, and since #592 (gate 391 D17, Session 79.21b)
  // a RECORDED one. It wrote `cancelled` straight from the browser with no
  // reason, no name, no time, and told nobody. It now goes through
  // cancel_material_request(), which writes who, when and why in one call, and
  // cancelMaterialRequest() tells the person who asked and the manager who
  // approved it, with the reason, and clears the bells that pointed at it.
  // The row drops out of the active queue on the next load, as before.
  async function submitCancel(reason) {
    if (!cancellingReq) return;
    setCancelSaving(true);
    setCancelError('');
    const r = await cancelMaterialRequest({
      request: cancellingReq, reason, cancellerName: profile?.full_name || null, cancellerId: profile?.id || null,
    });
    setCancelSaving(false);
    // The database's own sentence, in the dialog -- the typed reason survives.
    if (r.error) { setCancelError(r.error.message); return; }
    setCancellingReq(null);
    setSelectedReq(null);
    setMobileDetailReq(null);
    await load();
  }

  // #256. Approved-to-order rows sort to the top of Needs Action: the spend decision is
  // already made and the only thing standing between them and a placed order is somebody
  // calling the vendor. Within each group the load() ordering (oldest first) is preserved,
  // because sort() is stable.
  const needsAction = requests
    .filter(r => r.status === 'approved')
    .slice()
    .sort((a, b) => (b.approved_at != null ? 1 : 0) - (a.approved_at != null ? 1 : 0));
  const ordered = requests.filter(r => r.status === 'ordered');
  const needsApproval = requests.filter(r => r.status === 'pending_purchase_approval');

  const oldestPending = needsAction.length > 0
    ? Math.max(...needsAction.map(r => ageDays(r.created_at)))
    : 0;

  const canContact = canContactVendor({ isAdmin, isManager, isExecutive, isPurchasing, isTeamMember, profile }, settings);
  const canAction = canActionPurchasingQueue({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  // Callouts gate Wave 2 — pur_queue_tabs_first. Purchasing and above, which is
  // exactly who can work this queue, so the existing predicate is the gate.
  const needsActionTabRef = useRef(null);
  // #288. Its own predicate rather than reusing canAction at the call site, so
  // "may this person declare receiving finished" can change without hunting
  // through render code for a borrowed boolean.
  const canShortClose = canCloseLineForReceiving({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  const sharedCloseProps = { canShortClose, onShortClose: openShortClose, onReopen: reopenLineForReceiving };
  const sharedShortCloseProps = {
    visible: !!shortCloseItem,
    flag: RECEIPT,
    item: shortCloseItem,
    req: shortCloseReq,
    isMobile,
    saving: shortCloseSaving,
    error: shortCloseError,
    done: shortCloseDone,
    result: shortCloseResult,
    onSubmit: submitShortClose,
    onClose: closeShortCloseSheet,
  };
  const canApprovePurchase = canApprovePurchaseThreshold({ isAdmin, isExecutive });

  const liveSelectedReq = selectedReq ? (requests.find(r => r.id === selectedReq.id) || selectedReq) : null;
  const liveMobileDetailReq = mobileDetailReq ? (requests.find(r => r.id === mobileDetailReq.id) || mobileDetailReq) : null;
  // #415, Session 79.14f -- the card can record the vendor's order number now,
  // so it needs the same saver the two detail panels already use. ONE handler,
  // three surfaces: a second copy is how the card and the panel start disagreeing.
  const sharedCardProps = { isMobile, selectedReq, setSelectedReq, setMobileDetailReq, openMarkOrdered, openTracking, openApproval, canAction, canApprovePurchase, onSaveVendorOrderRef: saveVendorOrderRef };
  // Marks the one chip the person actually typed, so the eye goes straight to
  // the number they were holding rather than reading all six.
  const leadKeyFor = r => (searching ? matchedIdentifierKey(r, search) : null);
  const sharedMoProps = {
    // #614 -- a request sent to one of our own branches leaves Needs action.
    onSentAsTransfer: () => { load(); },
    visible: showMarkOrdered, req: markOrderedReq, isMobile,
    moVendor, setMoVendor,
    moVendorSearch, setMoVendorSearch,
    moVendorResults, setMoVendorResults,
    moPrice, setMoPrice,
    moPackQty, setMoPackQty,
    moPackUnit, setMoPackUnit,
    moPacksToOrder, setMoPacksToOrder,
    moPoRef, setMoPoRef,
    moPoGenerated, moPoIssue, setMoPoIssue,
    moVendorOrderRef, setMoVendorOrderRef,
    moEta, setMoEta,
    moVendorItemNum, setMoVendorItemNum,
    moItemState, toggleMoItemChecked, setMoItemPrice, setMoItemPacks,
    moSaving, moError, moDone, moPriceWarning,
    settings,
    branchRows,
    moSendMode, setMoSendMode,
    moVendorNote, setMoVendorNote,
    moSendPo, setMoSendPo,
    moSendConfirmation, setMoSendConfirmation,
    moSendResult,
    onSubmit: submitMarkOrdered,
    onClose: () => { setShowMarkOrdered(false); setSelectedReq(null); },
    canContact,
  };
  const sharedTrackProps = {
    visible: showTracking, req: trackingReq, item: trackingItem, isMobile, trackCleared, onClear: submitClearTracking,
    trackCarrier, setTrackCarrier,
    trackNumber, setTrackNumber,
    trackEta, setTrackEta,
    trackSaving, trackError, trackDone,
    onSubmit: submitTracking,
    onClose: () => setShowTracking(false),
  };
  const sharedCancelProps = {
    req: cancellingReq, isMobile,
    saving: cancelSaving, error: cancelError,
    onConfirm: submitCancel,
    onClose: () => { if (!cancelSaving) { setCancellingReq(null); setCancelError(''); } },
  };
  const sharedApprovalProps = {
    visible: showApproval, req: approvalReq, isMobile,
    denyReason, setDenyReason,
    showDenyBox, setShowDenyBox,
    approvalSaving, approvalError, approvalDone,
    onApprove: submitApprovePurchase,
    onDeny: submitDenyPurchase,
    onClose: () => setShowApproval(false),
  };

  // The three lists above feed the STAT BAR and must stay unfiltered: those
  // figures describe the state of the queue, not the state of your search. A
  // buyer typing a PO number has not made the other six orders stop needing
  // action. The TAB counts below are filtered, because while a search is
  // running the useful question is which tab holds the match.
  const matchesSearch = r => matchesOrderSearch(r, search);
  const filt = list => (searching ? list.filter(matchesSearch) : list);
  const needsActionShown = filt(needsAction);
  const needsApprovalShown = filt(needsApproval);
  const orderedShown = filt(ordered);

  const tabCounts = {
    needs_action: needsActionShown.length,
    pending_approval: needsApprovalShown.length,
    on_order: orderedShown.length,
  };
  const listForTab = activeTab === 'needs_action' ? needsActionShown
    : activeTab === 'pending_approval' ? needsApprovalShown
      : orderedShown;
  const liveMatchCount = needsActionShown.length + needsApprovalShown.length + orderedShown.length;

  // Each tab now owns its own empty state. The old single "Queue is clear" was a
  // page-level claim rendered between two sections that could each still hold
  // rows — #255's shape, and it took a Gate 6 read to find. A per-tab message
  // cannot make that mistake, because it only ever describes the list under it.
  const EMPTY_STATES = {
    needs_action: ['Nothing needs action', 'Approved requests waiting to be ordered land here'],
    pending_approval: ['No spend approvals pending', 'Orders over the threshold wait here for an executive'],
    on_order: ['Nothing on order', 'Placed orders wait here until everything arrives'],
  };
  const [emptyTitle, emptySub] = EMPTY_STATES[activeTab];

  // A search that finds nothing and a tab that is genuinely clear are the same
  // screen and opposite meanings: the first is a dead end the reader can fix by
  // typing something else, the second is good news. RequestsInboxPage.js makes
  // the same distinction and for the same reason.
  // COLBY, ON THE LIVE SCREEN, and he was right: "why does this page say nothing
  // matches that PO number when it does?" The first version of this said
  // "Nothing on this tab matches" whenever the active tab came back empty. That
  // sentence is LITERALLY TRUE and reads as NOT FOUND — and it was rendering
  // directly above the settled order the search had just found, which he had
  // open in the panel beside it. A screen that contradicts what is on it.
  //
  // So the empty state has to know whether the SEARCH failed or only this TAB
  // did, and those are opposite meanings: one is a dead end, the other is a
  // signpost. A found-elsewhere result gets a quiet line rather than a big card,
  // because a bordered panel in the middle of the column reads as failure
  // however it is worded.
  const foundElsewhere = liveMatchCount + settledRows.length;
  const emptyState = searching ? (
    settledLoading ? (
      <div style={{ fontSize: 11, color: '#9A9A9A', padding: '6px 2px' }}>Searching…</div>
    ) : foundElsewhere > 0 ? (
      <div style={{ fontSize: 11.5, color: '#5C5C5C', padding: '6px 2px 2px', lineHeight: 1.5 }}>
        Nothing on this tab — {settledRows.length > 0 && liveMatchCount === 0
          ? <>found {settledRows.length === 1 ? 'it' : `${settledRows.length}`} under <strong>Settled orders</strong> below.</>
          : liveMatchCount > 0 && settledRows.length === 0
            ? <>{liveMatchCount} match{liveMatchCount === 1 ? '' : 'es'} on another tab, and the counts above show which.</>
            : <>{liveMatchCount} on another tab, {settledRows.length} under <strong>Settled orders</strong> below.</>}
      </div>
    ) : (
      <div style={{ background: 'white', borderRadius: 10, padding: '24px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#5C5C5C' }}>No order matches “{search.trim()}”</div>
        <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 4 }}>
          Searches the request number, PO, vendor order number, work order, invoice number and tracking number — open and settled.
        </div>
      </div>
    )
  ) : (
    <div style={{ background: 'white', borderRadius: 10, padding: '32px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#9A9A9A' }}>{emptyTitle}</div>
      <div style={{ fontSize: 11, color: '#C0C0C0', marginTop: 4 }}>{emptySub}</div>
    </div>
  );

  // D12. 44px on a phone, which is #513's touch minimum and the size a buyer
  // taking a vendor call needs. NOTE the deliberate absence of a fontSize:
  // index.css sets every text input to 16px globally, which is locked rule 12 —
  // anything smaller makes iOS Safari zoom the whole page on focus.
  //
  // D-A. It sits ABOVE the tabs rather than inside one, because it searches
  // across all of them and reaches settled orders as well. Its position says so
  // without a word of explanation.
  const searchBox = (
    <div style={{ position: 'relative', padding: isMobile ? '10px 12px' : '12px 14px 0', background: isMobile ? 'white' : 'transparent' }}>
      <IconSearch size={15} color="#9A9A9A" style={{ position: 'absolute', left: isMobile ? 24 : 26, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
      <input
        type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search any order number..."
        aria-label="Search orders by any identifier"
        style={{
          width: '100%', height: isMobile ? 44 : 38, border: '1px solid #E0E0E0', borderRadius: 8,
          padding: '0 34px 0 34px', color: '#0F0F0F', background: 'white', boxSizing: 'border-box',
        }}
      />
      {searching && (
        <button
          type="button" onClick={() => setSearch('')} aria-label="Clear search"
          style={{
            position: 'absolute', right: isMobile ? 20 : 22, top: '50%', transform: 'translateY(-50%)',
            width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#F0F0F0', border: 'none', borderRadius: '50%', cursor: 'pointer', color: '#5C5C5C', padding: 0,
          }}
        >
          <IconX size={13} />
        </button>
      )}
    </div>
  );

  // D7. Settled orders come back IN the list, marked, rather than on a fourth
  // tab — a fourth tab is permanent cost for an occasional need and would sit
  // there looking empty most days. D-D: they are visibly a different kind of
  // thing, so a result can never be mistaken for something needing attention.
  const settledSection = !searching ? null : (
    <div style={{ marginTop: liveMatchCount > 0 ? 14 : 8 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8,
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
        color: '#7A7A7A', marginBottom: 6,
      }}>
        <span>Settled orders</span>
        {settledLoading && <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>searching…</span>}
      </div>
      {settledLoading ? null : settledRows.length === 0 ? (
        <div style={{ fontSize: 11, color: '#9A9A9A', padding: '2px 2px 6px' }}>
          {liveMatchCount > 0 ? 'None settled — the matches are above.' : `No settled order matches “${search.trim()}”.`}
        </div>
      ) : (
        <>
          {settledRows.map(r => (
            <div key={`settled-${r.id}`} style={{ opacity: 0.92 }}>
              <div style={{
                display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
                color: '#5C5C5C', background: '#EFEFEF', border: '1px solid #DCDCDC',
                borderRadius: 4, padding: '1px 6px', marginBottom: 3,
              }}>SETTLED</div>
              <RequestCard req={r} {...sharedCardProps} leadKey={leadKeyFor(r)} />
            </div>
          ))}
          {/* D9. The cap is STATED rather than left to look like the whole
              truth — accounts payable's Settled tab set that precedent at the
              same number. */}
          {settledCapped && (
            <div style={{ fontSize: 11, color: '#7A7A7A', padding: '2px 2px 6px' }}>
              Showing the {SETTLED_CAP} most recent. Narrow the search to see older ones.
            </div>
          )}
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 80 }}>
        {/* Session 79.14, on Colby's reading of the live screen: the summary strip
            that used to sit here said "7 needs action | 8 on order | 11d old", and
            TWO OF THOSE THREE WERE ALREADY IN THE TABS DIRECTLY BELOW IT. It was
            also INCOMPLETE - it never showed the spend-approval count, which is
            the one tab a buyer cannot see from anywhere else. So it repeated what
            was known, omitted what was not, and cost a whole band of a phone
            screen on the priority surface.

            What it alone carried is the AGE OF THE OLDEST WAITING REQUEST, and on
            a chase-the-vendor screen that is the thing worth shouting about. It
            now gets its own line INSTEAD OF being buried between two counts - and
            only when it is actually bad. Below three days there is nothing here at
            all and the header is a single band.

            THE TABS NOW CARRY THE position: sticky THE STRIP USED TO, which was
            arguably backwards before - the strip claimed the top while the
            NAVIGATION scrolled away.

            IT DID NOT ACTUALLY STICK UNTIL THIS SESSION, AND NEITHER DID ANY
            OTHER IN-PAGE HEADER IN THE APP. `.main-content` carried
            `overflow-x: hidden`, and a non-visible overflow-x forces overflow-y
            from `visible` to `auto` - which makes that element a scroll
            container, and a sticky element sticks to its nearest scroll
            container rather than to the page. That container never scrolls (the
            document does), so nothing inside it could stick: 19 declarations
            across 14 files. `body` already carried a comment about this exact
            trap, written when it broke `.topbar`; `.main-content` was never
            swept. Fixed in index.css - see #540.

            `top` is the TOPBAR HEIGHT, not 0. The topbar is itself sticky at
            top: 0, so a header pinned at 0 slides underneath it and is invisible
            - which is what happened on the first attempt, and it was only
            visible by looking at the screen rather than at the measurement,
            which said top: 0 and read as success.

            Three days is the existing threshold this screen already used to turn
            that figure amber, not a new judgement. */}
        <div style={{ position: 'sticky', top: 'var(--topbar-height)', zIndex: 10 }}>
          {searchBox}
          <QueueTabs activeTab={activeTab} setActiveTab={setActiveTab} counts={tabCounts} isMobile needsActionTabRef={needsActionTabRef} />
          {oldestPending >= 3 && (
            <div style={{ background: '#FAEEDA', borderBottom: '1px solid #EFDFBE', padding: '5px 14px', fontSize: 11, fontWeight: 600, color: '#854F0B' }}>
              Oldest has waited {oldestPending} day{oldestPending === 1 ? '' : 's'}
            </div>
          )}
        </div>

        {/* Callouts gate Wave 2 — pur_queue_tabs_first. The three tabs are ordered
            by whose court the ball is in rather than by status name, which is the
            whole model of this screen and is nowhere on it. */}
        <OnboardingCallout
          calloutKey="pur_queue_tabs_first"
          text="These tabs follow whose turn it is, not status — Needs action is yours, On order is the vendor's."
          anchorRef={null}
          isMobile={true}
          show={canAction}
        />


        <div style={{ padding: '10px 12px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
          ) : error ? (
            <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>{error}</div>
          ) : listForTab.length === 0 ? (<>{emptyState}{settledSection}</>) : (
            <>
              {activeTab === 'needs_action' && !searching && (
                <div style={{ fontSize: 11, color: '#9A9A9A', marginBottom: 8 }}>Tap a card to view details</div>
              )}
              {listForTab.map(r => (
                <div key={r.id}>
                  <RequestCard req={r} {...sharedCardProps} leadKey={leadKeyFor(r)} />
                  {activeTab === 'needs_action' && canAction && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: -2, marginBottom: 10 }}>
                      <button onClick={() => openMarkOrdered(r)}
                        style={{ padding: '9px 0', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Mark ordered
                      </button>
                      <button
                        onClick={() => setCancellingReq(r)}
                        style={{ padding: '9px 0', background: 'white', color: '#A32D2D', border: '1px solid #F09595', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {settledSection}
            </>
          )}
        </div>

        {snackbar && (
          <Snackbar msg={snackbar.msg} undoFn={snackbar.undoFn} onDismiss={dismissSnackbar} isMobile />
        )}
        <MarkOrderedSheet {...sharedMoProps} />
        <TrackingSheet {...sharedTrackProps} />
        <ApprovalSheet {...sharedApprovalProps} />
        <CancelConfirmSheet {...sharedCancelProps} />
        <ShortCloseSheet {...sharedShortCloseProps} />
        {mobileDetailReq && (
          <MobileDetailSheet
            req={liveMobileDetailReq}
            leadKey={liveMobileDetailReq ? leadKeyFor(liveMobileDetailReq) : null}
            onClose={() => setMobileDetailReq(null)}
            openMarkOrdered={openMarkOrdered}
            openTracking={openTracking}
            openApproval={openApproval}
            onCancelRequest={setCancellingReq}
            canContact={canContact}
            canAction={canAction}
            canApprovePurchase={canApprovePurchase}
            onSaveVendorOrderRef={saveVendorOrderRef}
            {...sharedCloseProps}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', borderBottom: '1px solid #E8E8E8', background: '#FAFAF9', flexShrink: 0 }}>
        <div style={{ padding: '12px 20px', borderRight: '1px solid #E8E8E8', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: needsAction.length > 0 ? '#9A5F00' : '#0F0F0F' }}>{needsAction.length}</div>
          <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>Needs action</div>
        </div>
        <div style={{ padding: '12px 20px', borderRight: '1px solid #E8E8E8', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: needsApproval.length > 0 ? '#993556' : '#0F0F0F' }}>{needsApproval.length}</div>
          <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>Pending spend approval</div>
        </div>
        <div style={{ padding: '12px 20px', borderRight: '1px solid #E8E8E8', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#0F0F0F' }}>{ordered.length}</div>
          <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>On order</div>
        </div>
        <div style={{ padding: '12px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: oldestPending >= 3 ? '#9A5F00' : '#0F0F0F' }}>
            {oldestPending > 0 ? oldestPending + 'd' : '—'}
          </div>
          <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>Oldest pending</div>
        </div>
      </div>

      {/* Callouts gate Wave 2 — pur_queue_tabs_first. The three tabs are ordered
          by whose court the ball is in rather than by status name, which is the
          whole model of this screen and is nowhere on it. */}
      <OnboardingCallout
        calloutKey="pur_queue_tabs_first"
        text="These tabs follow whose turn it is, not status — Needs action is yours, On order is the vendor's."
        anchorRef={needsActionTabRef}
        isMobile={false}
        show={canAction}
      />


      <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr', flex: 1, overflow: 'hidden' }}>
        {/* Session 79.14d, on Colby's reading of the live screen: the search box
            and the tab strip both GOVERN THIS LIST and both used to be rendered
            across the full page width, so "On order" straddled the column border
            — measured at 429 to 537 against a border at 500. They live in the
            column now and end where it ends. Header and list are separate boxes
            so the controls stay put while the list scrolls under them. */}
        <div style={{ borderRight: '1px solid #E8E8E8', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#F7F7F6' }}>
          <div style={{ flexShrink: 0 }}>
            {searchBox}
            <QueueTabs activeTab={activeTab} setActiveTab={setActiveTab} counts={tabCounts} isMobile={false} needsActionTabRef={needsActionTabRef} compact />
          </div>
          <div style={{ overflowY: 'auto', padding: '14px 14px 80px', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
          ) : error ? (
            <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>{error}</div>
          ) : (
            <>
              {listForTab.length === 0
                ? emptyState
                : listForTab.map(r => <RequestCard key={r.id} req={r} {...sharedCardProps} leadKey={leadKeyFor(r)} />)}
              {settledSection}
            </>
          )}
          </div>
        </div>

        <div style={{ overflowY: 'auto' }}>
          <DetailPanel req={liveSelectedReq} leadKey={liveSelectedReq ? leadKeyFor(liveSelectedReq) : null} openMarkOrdered={openMarkOrdered} openTracking={openTracking} openApproval={openApproval} onCancelRequest={setCancellingReq} canContact={canContact} canAction={canAction} canApprovePurchase={canApprovePurchase} onSaveVendorOrderRef={saveVendorOrderRef} {...sharedCloseProps} />
        </div>
      </div>

      {snackbar && (
        <Snackbar msg={snackbar.msg} undoFn={snackbar.undoFn} onDismiss={dismissSnackbar} isMobile={false} />
      )}
      <MarkOrderedSheet {...sharedMoProps} />
      <TrackingSheet {...sharedTrackProps} />
      <ApprovalSheet {...sharedApprovalProps} />
      <CancelConfirmSheet {...sharedCancelProps} />
      <ShortCloseSheet {...sharedShortCloseProps} />
    </div>
  );
}
